import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, MessageSquareText } from "lucide-react";
import { IMAGES } from "@/data";

const LINKS = [
  { label: "Our Services", href: "#services" },
  { label: "Why Choose Us", href: "#why" },
  { label: "About Us", href: "#about" },
  { label: "FAQ", href: "#faq" },
  { label: "Reviews", href: "#reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = ["services", "why", "about", "faq", "reviews", "book"];
    const onScroll = () => {
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50"
    >
      <div
        style={{ backgroundColor: scrolled ? "hsl(258 10% 38%)" : "transparent" }}
        className={`absolute inset-0 transition-colors duration-300 ${
          scrolled ? "backdrop-blur-xl border-b border-white/10 shadow-md" : ""
        }`}
      />
      <nav className="relative mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center">
          <img src={scrolled ? IMAGES.logoHorizontalWhite : IMAGES.logoHorizontal} alt="Cheeky Pups" className="h-11 sm:h-[3.25rem] w-auto" />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => {
            const isActive = active === l.href.slice(1);
            const linkColor = scrolled
              ? isActive
                ? "text-white"
                : "text-white/75 hover:text-white"
              : isActive
                ? "text-accent"
                : "text-primary hover:text-accent";
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className={`text-base font-semibold transition-colors relative group ${linkColor}`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 rounded-full transition-all duration-300 ${
                    scrolled ? "bg-white" : "bg-accent"
                  } ${isActive ? "w-full" : "w-0 group-hover:w-full"}`}
                />
              </a>
            );
          })}
          <a
            href="#book"
            data-testid="nav-book-now"
            className={`rounded-full px-5 py-2.5 text-sm font-bold hover:-translate-y-0.5 hover:shadow-lg transition-all ${
              scrolled ? "bg-white text-primary" : "bg-primary text-primary-foreground hover:shadow-primary/25"
            }`}
          >
            Book Now
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 ${scrolled ? "text-white" : "text-primary"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className={`md:hidden backdrop-blur-xl border-b ${scrolled ? "bg-primary border-white/10" : "bg-background/95 border-border"}`}>
          <div className="px-5 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                className={`text-base font-semibold ${
                  scrolled
                    ? active === l.href.slice(1) ? "text-white" : "text-white/80 hover:text-white"
                    : active === l.href.slice(1) ? "text-accent" : "text-primary/80 hover:text-accent"
                }`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-book-now"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground w-fit"
            >
              <MessageSquareText size={16} /> Book Now
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
